<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h2 class="text-2xl font-bold text-foreground">
        {{ $t('vet.signup.owner.title') }}
      </h2>
      <p class="text-muted-foreground">
        {{ $t('vet.signup.owner.description') }}
      </p>
    </div>

    <!-- Error Alert -->
    <UiAlert v-if="serverError" variant="destructive">
      <Icon name="tabler:alert-circle" size="16" />
      <UiAlertDescription>{{ serverError }}</UiAlertDescription>
    </UiAlert>

    <!-- Form Fields -->
    <VeeForm ref="formRef" :validation-schema="ownerSchema" @submit="handleSubmit" class="space-y-4" v-slot="{ meta }">
      <!-- Full Name -->
      <div class="space-y-2">
        <UiLabel for="ownerName">
          {{ $t('vet.signup.owner.name') }}
          <span class="text-destructive">*</span>
        </UiLabel>
        <VeeField name="ownerName" v-slot="{ field, errorMessage }">
          <UiInput
            id="ownerName"
            v-bind="field"
            type="text"
            :placeholder="$ts('vet.signup.owner.namePlaceholder')"
            autocomplete="name"
          />
          <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
        </VeeField>
      </div>

      <!-- Email -->
      <div class="space-y-2">
        <UiLabel for="email">
          {{ $t('vet.signup.owner.email') }}
          <span class="text-destructive">*</span>
        </UiLabel>
        <VeeField name="email" v-slot="{ field, errorMessage }">
          <UiInput
            id="email"
            v-bind="field"
            type="email"
            :placeholder="$ts('vet.signup.owner.emailPlaceholder')"
            autocomplete="email"
          />
          <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
        </VeeField>
      </div>

      <!-- Phone (Optional) -->
      <div class="space-y-2">
        <div class="flex items-center justify-between">
          <UiLabel for="phone">{{ $t('vet.signup.owner.phone') }}</UiLabel>
          <span class="text-xs text-muted-foreground">{{ $t('vet.signup.owner.phoneOptional') }}</span>
        </div>
        <VeeField name="phone" v-slot="{ field }">
          <UiInput
            id="phone"
            v-bind="field"
            type="tel"
            :placeholder="$ts('vet.signup.owner.phonePlaceholder')"
            autocomplete="tel"
          />
        </VeeField>
      </div>

      <!-- Password -->
      <div class="space-y-2">
        <UiLabel for="password">
          {{ $t('vet.signup.owner.password') }}
          <span class="text-destructive">*</span>
        </UiLabel>
        <VeeField name="password" v-slot="{ field, errorMessage }">
          <UiInput
            id="password"
            v-bind="field"
            type="password"
            :placeholder="$ts('vet.signup.owner.passwordPlaceholder')"
            autocomplete="new-password"
          />
          <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
        </VeeField>
      </div>

      <!-- Confirm Password -->
      <div class="space-y-2">
        <UiLabel for="confirmPassword">
          {{ $t('vet.signup.owner.confirmPassword') }}
          <span class="text-destructive">*</span>
        </UiLabel>
        <VeeField name="confirmPassword" v-slot="{ field, errorMessage }">
          <UiInput
            id="confirmPassword"
            v-bind="field"
            type="password"
            :placeholder="$ts('vet.signup.owner.confirmPasswordPlaceholder')"
            autocomplete="new-password"
          />
          <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
        </VeeField>
      </div>

      <!-- Terms and Privacy -->
      <div class="space-y-2">
        <VeeField name="acceptedTerms" v-slot="{ value, handleChange, errorMessage }" type="checkbox" :unchecked-value="false">
          <div class="flex items-start gap-3">
            <RekaCheckbox
              :checked="value"
              class="mt-0.5"
              @update:checked="handleChange"
            />
            <label class="text-sm text-muted-foreground leading-relaxed cursor-pointer" @click="handleChange(!value)">
              <i18n-t keypath="vet.signup.owner.termsAgreement">
                <template #terms>
                  <NuxtLink :to="$localePath('/legal/terms')" class="text-bubba-blue hover:underline" target="_blank" rel="noopener noreferrer">
                    {{ $t('vet.signup.owner.termsLink') }}
                  </NuxtLink>
                </template>
                <template #privacy>
                  <NuxtLink :to="$localePath('/legal/privacy')" class="text-bubba-blue hover:underline" target="_blank" rel="noopener noreferrer">
                    {{ $t('vet.signup.owner.privacyLink') }}
                  </NuxtLink>
                </template>
              </i18n-t>
            </label>
          </div>
          <p v-if="errorMessage" class="text-sm text-destructive mt-1">{{ errorMessage }}</p>
        </VeeField>
      </div>

      <!-- Marketing — optional, checked by default -->
      <div class="flex items-start gap-3">
        <RekaCheckbox
          :checked="agreeMarketing"
          class="mt-0.5"
          @update:checked="(val: boolean) => { agreeMarketing = val; }"
        />
        <label class="text-sm text-muted-foreground leading-relaxed cursor-pointer" @click="agreeMarketing = !agreeMarketing">
          {{ $t('auth.register.agreeMarketing') }}
        </label>
      </div>

      <!-- Analytics — optional, checked by default -->
      <div class="flex items-start gap-3">
        <RekaCheckbox
          :checked="agreeAnalytics"
          class="mt-0.5"
          @update:checked="(val: boolean) => { agreeAnalytics = val; }"
        />
        <label class="text-sm text-muted-foreground leading-relaxed cursor-pointer" @click="agreeAnalytics = !agreeAnalytics">
          {{ $t('auth.register.agreeAnalytics') }}
        </label>
      </div>

      <!-- Navigation -->
      <div class="flex justify-between pt-4">
        <UiButton type="button" variant="outline" :disabled="isLoading" @click="$emit('back')">
          <Icon name="tabler:arrow-left" size="16" />
          {{ $t('vet.signup.navigation.back') }}
        </UiButton>
        <UiButton
          type="submit"
          :disabled="isLoading || !meta.valid"
          class="bg-bubba-blue hover:bg-bubba-blue/90"
        >
          <Icon v-if="isLoading" name="tabler:loader-2" size="16" class="animate-spin" />
          {{ isLoading ? $t('vet.signup.navigation.submitting') : $t('vet.signup.navigation.submit') }}
        </UiButton>
      </div>
    </VeeForm>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';
import { useI18n } from '#imports';
import type { SignupFormData } from '#composables/vet/useVetSignupWizard';
import { useConsentState } from '#bubba/shared/composables/consent/useConsentState';
import { createLogger } from '#bubba/shared/utils/logger';

const log = createLogger('StepOwnerDetails', { enabled: false });

const props = defineProps<{
  formData: SignupFormData;
}>();

const emit = defineEmits<{
  update: [data: Partial<SignupFormData>];
  submit: [];
  back: [];
}>();

const { $t, $ts, $localePath } = useI18n();
const { savePreferences } = useConsentState();

// Consent checkboxes — marketing & analytics default to checked
const agreeMarketing = ref(true);
const agreeAnalytics = ref(true);

const ownerSchema = toTypedSchema(
  z.object({
    ownerName: z.string().min(1, $ts('vet.signup.owner.nameRequired')),
    email: z.string()
      .min(1, $ts('vet.signup.owner.emailRequired'))
      .email($ts('vet.signup.owner.emailInvalid')),
    phone: z.string().optional(),
    password: z.string()
      .min(1, $ts('vet.signup.owner.passwordRequired'))
      .min(8, $ts('vet.signup.owner.passwordMin')),
    confirmPassword: z.string().min(1, $ts('vet.signup.owner.confirmPasswordRequired')),
    acceptedTerms: z.literal(true, {
      error: $ts('vet.signup.owner.termsRequired'),
    }),
  }).refine((data) => data.password === data.confirmPassword, {
    message: $ts('vet.signup.owner.passwordMismatch'),
    path: ['confirmPassword'],
  })
);

const formRef = ref();
const isLoading = ref(false);
const serverError = ref<string | null>(null);

async function handleSubmit(values: Record<string, unknown>) {
  isLoading.value = true;
  serverError.value = null;

  // Persist consent choices
  savePreferences({
    marketing: agreeMarketing.value,
    analytics: agreeAnalytics.value,
    third_party: false,
  });
  log.debug('Persisted registration consent choices');

  // Update form data with owner details
  emit('update', {
    ownerName: values.ownerName as string,
    email: values.email as string,
    phone: values.phone as string | undefined,
    password: values.password as string,
    acceptedTerms: true,
  });

  // Trigger the submit event to let the parent handle account creation
  emit('submit');
}

// Expose loading state for parent to control
defineExpose({
  setLoading: (loading: boolean) => { isLoading.value = loading; },
  setError: (error: string | null) => { serverError.value = error; },
});
</script>
